<template>
  <div class="missing-block-form d-flex flex-column" v-if="!isSuccess">
    <div class="title mb-2">
      {{ $t('Предложите новый блок') }}
    </div>
    <div class="sub-title mb-5">
      {{ $t('Мы изучаем все идеи и предложения') }}
    </div>
    <v-textarea
        v-model="text"
        variant="outlined"
        :placeholder="$t('Опишите идею нового блока')"
        :error-messages="textError ? $t(textError) : ''"
        :hide-details="!textError"
        rounded="lg"
        no-resize
        maxlength="350"
        clearable
        class="mb-3"
        @update:model-value="clearTextError"
    >
      <template v-slot:clear>
        <v-hover>
          <template v-slot:default="{ props }">
            <v-icon :icon="ClearIcon" v-bind="props" @click="text = ''; clearTextError()" v-if="text"/>
          </template>
        </v-hover>
      </template>
    </v-textarea>
    <v-checkbox
        v-model="callAgreement"
        :label="$t('Хочу обсудить идею по телефону')"
        :false-icon="FalseIcon"
        :true-icon="TrueIcon"
        hide-details
        class="missing-block-checkbox mb-3"
    />
    <div
        v-if="callAgreement"
        class="mb-3 phone-field"
        :class="{ 'phone-field--error': phoneError }"
    >
      <div class="phone-field__control">
        <span class="phone-field__prefix">{{ phonePrefix }}</span>
        <input
            ref="phoneInput"
            :value="phoneDisplay"
            inputmode="numeric"
            autocomplete="tel"
            class="phone-field__input"
            @keydown="allowPhoneKey"
            @input="formatPhoneInput"
            @focus="normalizePhoneSelection"
            @click="normalizePhoneSelection"
        />
        <v-icon
            v-if="phoneDigits"
            :icon="ClearIcon"
            class="phone-field__clear"
            @click="resetPhone"
        />
      </div>
      <div v-if="phoneError" class="phone-field__error">
        {{ $t(phoneError) }}
      </div>
    </div>
    <v-btn
        variant="flat"
        color="primary"
        rounded="lg"
        class="w-100 missing-block-submit"
        :loading="isLoading"
        :disabled="isLoading"
        @click="sendMissingBlock"
    >
      {{ $t('Отправить') }}
    </v-btn>
  </div>
  <div class="text-center position-relative mb-5" v-else>
    <v-icon :icon="SuccessIcon" size="40"/>
    <div class="title mt-2 mb-2">
      {{ $t('Готово!') }}
    </div>
    <div class="d-flex justify-center text-center mb-4">
      <div class="delete-description">
        {{ $t('Предложение отправлено') }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, nextTick, onMounted, ref, watch} from "vue";
import {useRoute} from "vue-router";
import ClearIcon from "~/assets/icons/clear.vue";
import TrueIcon from "~/assets/icons/checkbox/true.vue";
import FalseIcon from "~/assets/icons/checkbox/false.vue";
import SuccessIcon from "~/assets/icons/success.vue";

const route = useRoute()
const tapApi = useTapApi()
const text = ref('')
const textError = ref('')
const phoneError = ref('')
const phonePrefix = '+7'
const phoneDigits = ref('')
const phoneInput = ref<HTMLInputElement | null>(null)
const phoneDigitPositions = [1, 2, 4, 5, 6, 8, 9, 11, 12]
const callAgreement = ref(false)
const isLoading = ref(false)
const isSuccess = ref(false)
const firstName = ref('')
const lastName = ref('')

const message = computed(() => text.value.trim())
const alias = computed(() => {
  const value = route.params.view || route.params.alias
  return Array.isArray(value) ? value[0] : value || ''
})
const isMessageValid = computed(() => message.value.length >= 15 && message.value.length <= 350)
const fullName = computed(() => [firstName.value, lastName.value].filter(Boolean).join(' '))
const phoneDisplay = computed(() => formatPhoneWithMask(phoneDigits.value))
const isPhoneValid = computed(() => phoneDigits.value.length === 9)
const phoneForRequest = computed(() => `+77${phoneDigits.value}`)

onMounted(() => {
  loadProfile()
})

watch(callAgreement, (value) => {
  if (value && !phoneDigits.value) {
    loadProfile()
    return
  }

  phoneError.value = ''
})

async function loadProfile() {
  try {
    const data = await tapApi.getProfileUser()
    firstName.value = data?.firstName || data?.firstname || ''
    lastName.value = data?.lastName || data?.lastname || ''

    if (data?.phone) {
      formatPhoneInput(data.phone)
    }
  } catch (e) {}
}

function clearTextError() {
  textError.value = ''
}

function formatPhoneWithMask(digits: string) {
  const padded = `7${digits}`.padEnd(10, '_')
  return `${padded.slice(0, 3)} ${padded.slice(3, 6)} ${padded.slice(6, 8)} ${padded.slice(8, 10)}`
}

function formatPhoneInput(value: string | Event) {
  const raw = typeof value === 'string' ? value : (value.target as HTMLInputElement)?.value || ''
  let digits = raw.replace(/\D/g, '')

  if (digits.length > 9) {
    if (digits.startsWith('77') || digits.startsWith('87')) {
      digits = digits.slice(2)
    } else if (digits.startsWith('7')) {
      digits = digits.slice(1)
    }
  }

  phoneDigits.value = digits.slice(0, 9)
  phoneError.value = ''
  nextTick(() => setPhoneCaret(phoneDigits.value.length))
}

function allowPhoneKey(event: KeyboardEvent) {
  if (
      event.ctrlKey ||
      event.metaKey ||
      ['Tab', 'Enter', 'Escape'].includes(event.key)
  ) {
    return
  }

  const input = event.target as HTMLInputElement
  const selection = getPhoneDigitSelection(input)

  if (event.key === 'Backspace') {
    event.preventDefault()

    if (selection.start !== selection.end) {
      replacePhoneDigits(selection.start, selection.end, '')
      return
    }

    if (selection.start > 0) {
      replacePhoneDigits(selection.start - 1, selection.start, '')
    }

    return
  }

  if (event.key === 'Delete') {
    event.preventDefault()

    if (selection.start !== selection.end) {
      replacePhoneDigits(selection.start, selection.end, '')
      return
    }

    replacePhoneDigits(selection.start, selection.start + 1, '')
    return
  }

  if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
    return
  }

  if (/^\d$/.test(event.key)) {
    event.preventDefault()

    if (selection.start !== selection.end || phoneDigits.value.length < 9) {
      replacePhoneDigits(selection.start, selection.end, event.key)
    }

    return
  }

  event.preventDefault()
}

function getPhoneDigitSelection(input: HTMLInputElement) {
  const start = input.selectionStart ?? 0
  const end = input.selectionEnd ?? start

  return {
    start: getPhoneDigitIndex(start),
    end: getPhoneDigitIndex(end)
  }
}

function getPhoneDigitIndex(caretPosition: number) {
  return phoneDigitPositions.filter((position) => position < caretPosition).length
}

function getPhoneCaretPosition(digitIndex: number) {
  if (digitIndex <= 0) return phoneDigitPositions[0]
  if (digitIndex >= phoneDigitPositions.length) return phoneDigitPositions[phoneDigitPositions.length - 1] + 1

  return phoneDigitPositions[digitIndex - 1] + 1
}

function replacePhoneDigits(start: number, end: number, value: string) {
  const nextValue = `${phoneDigits.value.slice(0, start)}${value}${phoneDigits.value.slice(end)}`.slice(0, 9)
  phoneDigits.value = nextValue
  phoneError.value = ''
  nextTick(() => setPhoneCaret(start + value.length))
}

function setPhoneCaret(digitIndex = phoneDigits.value.length) {
  const input = phoneInput.value

  if (!input || document.activeElement !== input) return

  const position = getPhoneCaretPosition(digitIndex)
  input.setSelectionRange(position, position)
}

function normalizePhoneSelection() {
  nextTick(() => {
    const input = phoneInput.value

    if (!input || document.activeElement !== input) return

    const start = input.selectionStart ?? 0
    const end = input.selectionEnd ?? start

    if (start === end && start < phoneDigitPositions[0]) {
      setPhoneCaret(0)
    }
  })
}

function resetPhone() {
  phoneDigits.value = ''
  phoneError.value = ''
  nextTick(() => setPhoneCaret(0))
}

async function sendMissingBlock() {
  textError.value = ''
  phoneError.value = ''
  let hasError = false

  if (!message.value.length) {
    textError.value = 'Опишите идею нового блока'
    hasError = true
  } else if (!isMessageValid.value) {
    textError.value = 'Количество символов должно быть от 15 до 350'
    hasError = true
  }

  if (callAgreement.value && !isPhoneValid.value) {
    phoneError.value = 'Введите номер телефона'
    hasError = true
  }

  if (hasError) {
    return
  }

  isLoading.value = true

  try {
    await tapApi.sendSuggestion(
        message.value,
        alias.value,
        callAgreement.value,
        phoneForRequest.value,
        fullName.value
    )
    isSuccess.value = true
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped lang="sass">
.missing-block-form
  padding-top: 20px

.title
  color: #000
  text-align: center
  font-size: 20px
  font-weight: 600
  line-height: normal

.sub-title
  color: #363636
  text-align: center
  font-size: 14px
  font-weight: 400
  line-height: normal

.phone-field
  &__control
    display: flex
    align-items: center
    width: 100%
    height: 40px
    min-height: 40px
    max-height: 40px
    padding: 0 12px
    border: 1px solid #CED4DA
    border-radius: 8px
    background: #fff
    box-sizing: border-box

    &:focus-within
      border-color: rgb(var(--v-theme-primary))

  &--error
    .phone-field__control
      border-color: #B20320

  &__prefix,
  &__input
    color: #363636
    font-family: inherit
    font-size: 14px
    font-weight: 400
    line-height: 20px
    letter-spacing: 0.5px

  &__prefix
    display: block
    flex: 0 0 auto
    height: 20px
    margin-right: 4px

  &__input
    display: block
    flex: 1 1 auto
    min-width: 0
    height: 20px
    padding: 0
    border: 0
    outline: 0
    background: transparent
    appearance: none
    -webkit-appearance: none

  &__clear
    flex: 0 0 auto
    margin-left: 8px
    color: #9AA1A9
    cursor: pointer

  &__error
    margin-top: 4px
    color: #B20320
    font-size: 12px
    line-height: 16px

:deep(.v-textarea .v-field)
  min-height: 110px

:deep(.v-textarea .v-input__control .v-field__clearable)
  top: -7px
  right: 4px

:deep(textarea)
  min-height: 78px
  color: #363636
  font-size: 14px
  line-height: 1.35

:deep(input)
  color: #363636
  font-size: 14px

:deep(textarea::placeholder),
:deep(input::placeholder)
  color: #363636
  opacity: 1

:deep(.v-checkbox .v-label)
  color: #363636
  font-size: 14px
  opacity: 1

.missing-block-checkbox
  margin-top: 0
  :deep(.v-selection-control)
    align-items: center
    min-height: 24px
  :deep(.v-selection-control__input)
    align-items: center
    justify-content: center
    width: 24px
    height: 24px
    margin-top: 0
    margin-bottom: 0
    overflow: visible
  :deep(.v-selection-control__wrapper)
    align-items: center
    height: 24px
    overflow: visible
  :deep(.v-icon)
    display: flex
    align-items: center
    justify-content: center
    width: 18px
    height: 18px
    line-height: 18px
    overflow: visible
  :deep(.v-label)
    line-height: 20px

:deep(.v-btn)
  min-height: 40px
  height: 40px
  font-size: 16px
  font-weight: 400

@media (max-width: 600px)
  .missing-block-submit
    margin-bottom: 24px
</style>
